import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, FileText, Settings, LogOut, Plus, Bell, Menu, X, LayoutGrid } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RivetWordmark, RivetMark } from "@/components/RivetLogo";
import { IDS } from "@/constants/testIds";
import { notifications as notifApi } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: IDS.nav.linkDashboard },
  { to: "/quotes", label: "Quotes", icon: FileText, testId: IDS.nav.linkQuotes },
  { to: "/templates", label: "Templates", icon: LayoutGrid, testId: "nav-link-templates" },
  { to: "/settings", label: "Settings", icon: Settings, testId: IDS.nav.linkSettings },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    notifApi.list().then((r) => setNotifs(r.data)).catch(() => {});
    const t = setInterval(() => notifApi.list().then((r) => setNotifs(r.data)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  return (
    <aside
      data-testid={IDS.nav.sidebar}
      className="w-64 shrink-0 h-full flex flex-col border-r border-slate-200 bg-white"
    >
      <div className="p-5 border-b border-slate-100">
        <RivetWordmark size={32} />
      </div>
      <div className="p-4">
        <button
          data-testid={IDS.nav.linkNewQuote}
          onClick={() => { nav("/quotes/new"); onNavigate?.(); }}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold px-4 py-3 transition-colors duration-200"
        >
          <Plus size={16} /> New Quote
        </button>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onNavigate}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#EFF6FF] text-[#2563EB]"
                    : "text-[#334155] hover:bg-slate-50 hover:text-[#001F3F]"
                }`
              }
            >
              <Icon size={18} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-3 pb-3">
        <button
          data-testid={IDS.nav.notifBell}
          onClick={() => nav("/dashboard#notifications")}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#334155] hover:bg-slate-50 transition-colors duration-200"
        >
          <div className="relative">
            <Bell size={18} />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </div>
          Notifications
        </button>
      </div>
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#2563EB] text-white text-sm font-bold flex items-center justify-center shrink-0">
            {user?.name?.[0]?.toUpperCase() || "R"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#001F3F] truncate">{user?.name}</div>
            <div className="text-xs text-[#64748B] truncate">{user?.email}</div>
          </div>
          <button
            data-testid={IDS.auth.logoutBtn}
            onClick={async () => { await logout(); nav("/"); }}
            className="text-[#64748B] hover:text-[#EF4444] transition-colors duration-200"
            aria-label="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-[#F1F5F9] text-[#001F3F]">
      <div className="hidden md:block">
        <SidebarContent />
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200/60 px-4 py-3 flex items-center justify-between">
          <RivetMark size={32} />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="p-2 rounded-md hover:bg-slate-100 transition-colors" aria-label="Open menu">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </header>
        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
